import React, { useState, useEffect, useMemo } from "react";
import { Card, Space, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import {
  AzureMap,
  AzureMapDataSourceProvider,
  AzureMapLayerProvider,
  AzureMapsProvider,
} from "react-azure-maps";
import { useNavigate } from "react-router-dom";
import atlas, { data } from "azure-maps-control";
import InteractivePopup from "./InteractivePopup";

const { Text, Paragraph } = Typography;

export default function Map({ clinics }) {
  const navigate = useNavigate();
  const [mapRef, setMapRef] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [popupOptions, setPopupOptions] = useState({});
  const [popupProperties, setPopupProperties] = useState({});
  const [collection, setCollection] = useState([]);
  const [center, setCenter] = useState([0, 0]);

  useEffect(() => {
    if (clinics && clinics.length > 0) {
      let sumLat = 0;
      let sumLon = 0;
      let featureCount = 0;

      // Build an array of map features, plus sum coords for averaging
      const features = clinics
        .filter(
          (clinic) =>
            clinic.location.coordinates.latitude !== null &&
            clinic.location.coordinates.longitude !== null
        )
        .map((clinic) => {
          sumLat += clinic.location.coordinates.latitude;
          sumLon += clinic.location.coordinates.longitude;
          featureCount++;
          return new atlas.data.Feature(
            new atlas.data.Point([
              clinic.location.coordinates.longitude,
              clinic.location.coordinates.latitude,
            ]),
            {
              clinicNo: clinic.clinicNo,
              clinicName: clinic.clinicName,
            }
          );
        });

      setCollection(features);

      const avgLat = sumLat / featureCount;
      const avgLon = sumLon / featureCount;
      setCenter([avgLon, avgLat]);
    } else {
      // If no clinics, reset center or handle as desired
      setCollection([]);
      setCenter([0, 0]);
    }
  }, [clinics]);

  useEffect(() => {
    if (mapRef && collection.length > 0) {
      // Build a bounding box that covers all features
      const bounds = atlas.data.BoundingBox.fromData({
        type: "FeatureCollection",
        features: collection,
      });
      // Set the map to these bounds with some padding
      mapRef.setCamera({
        center: center,
        bounds: bounds,
        padding: 50, // add a little space around edges
      });
    }
  }, [center, mapRef, collection]);

  useEffect(() => {
    const handleResize = () => {
      if (mapRef) {
        mapRef.resize();
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mapRef]);

  const mapOptions = useMemo(
    () => ({
      authOptions: {
        authType: "subscriptionKey",
        subscriptionKey: "ZANAQtcZvgznl2XpnxCVxcT4lsGe4iNrT4IGb0Owx70",
      },
      language: "en-CA",
      showFeedbackLink: false,
      showLogo: false,
      maxZoom: 22,
      view: "Auto",
    }),
    []
  );

  return (
    <AzureMapsProvider>
      <div style={{ height: "100%", width: "100%", position: "relative" }}>
        <AzureMap
          options={mapOptions}
          events={{
            ready: (map) => {
              setMapRef(map.map);

              map.map.events.add("click", (e) => {
                const clickedMyMarker = e.shapes.some((s) => {
                  // Ensure the shape has your custom data or an identifiable property
                  return s.data?.properties?.clinicNo !== undefined;
                });

                if (!clickedMyMarker) {
                  // The user clicked the map or some other non-marker shape
                  setIsVisible(false);
                }
              });
            },
          }}
        >
          <AzureMapDataSourceProvider
            id="SymbolLayerDataSource"
            collection={collection}
            options={{ cluster: true, clusterRadius: 50, clusterMaxZoom: 22 }}
          >
            <AzureMapLayerProvider
              type="SymbolLayer"
              filter={[
                "any",
                ["!", ["has", "point_count"]], // no cluster property
                ["==", ["get", "point_count"], 1], // single-item cluster
              ]}
              options={{
                iconOptions: {
                  image: "marker-darkblue",
                  allowOverlap: true,
                },
                textOptions: {
                  textField: ["get", "point_count_abbreviated"],
                  color: "white",
                  size: 18,
                  fontWeight: "bold",
                  anchor: "center",
                  offset: [0, -0.85],
                  allowOverlap: true,
                },
              }}
              events={{
                click: (e) => {
                  if (!mapRef || !e.shapes?.length) return;
                  const shape = e.shapes[0];
                  const props = shape.properties;

                  //if clustered zoom
                  if (props?.cluster) {
                    const clusterId = props.cluster_id;
                    const ds = mapRef.sources.getById("SymbolLayerDataSource");
                    ds.getClusterExpansionZoom(clusterId).then((zoom) => {
                      mapRef.setCamera({
                        center: shape.geometry.coordinates,
                        zoom: zoom,
                      });
                    });
                  } else if (e.shapes?.length) {
                    //if not clustered open popoup
                    const shape = e.shapes[0];
                    const [lng, lat] = shape.data.geometry.coordinates;

                    setPopupOptions({
                      // Position to the pin’s coordinate
                      position: new data.Position(lng, lat),
                      // Anchor the bottom of the popup to the pin
                      anchor: "bottom",
                      closeButton: false,

                      // Adjust as needed if you want it higher or lower
                      pixelOffset: [0, -20],
                    });
                    setPopupProperties(shape.data.properties);
                    setIsVisible(true);
                  }
                },
              }}
            />
          </AzureMapDataSourceProvider>

          {/* InteractivePopup replaces AzureMapPopup. 
              Pass isVisible and dynamic options so it opens at the right spot. */}
          <InteractivePopup
            isVisible={isVisible}
            options={popupOptions}
            style={{ boxShadow: "none" }}
            events={[
              {
                eventName: "close",
                callback: () => {
                  setIsVisible(false);
                },
              },
            ]}
          >
            {/* <PopupContent bgColor="white" /> */}
            <Card
              title="Clinic Information"
              size="small"
              bordered="false"
              extra={<CloseOutlined onClick={() => setIsVisible(false)} />}
              onClick={() => navigate(`/clinics/${popupProperties.clinicNo}`)}
              style={{
                width: "auto",
                minWidth: 200,
                boxShadow: "none", // Removes the default shadow
              }}
              // Give the body auto height
              bodyStyle={{
                padding: "12px",
                borderRadius: 2, // Make sure inside corners match
              }}
            >
              <Space direction="vertical" size="small">
                <Text strong>Clinic Name:</Text>
                <Paragraph>{popupProperties.clinicName || "N/A"}</Paragraph>

                <Text strong>Clinic No:</Text>
                <Paragraph>{popupProperties.clinicNo || "N/A"}</Paragraph>
              </Space>
            </Card>
          </InteractivePopup>
        </AzureMap>
      </div>
    </AzureMapsProvider>
  );
}
